"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { EyeOff, LogOut, Pencil, Plus, User } from "lucide-react";
import { usePathname } from "next/navigation";
import logo from "@/assets/marketsuite-favicon.png";
import { cn } from "@/lib/utils";

interface AdminBarProps {
	cmsURL?: string;
	collectionSlug?: string;
	id?: string;
	blogId?: string;
	draft?: boolean;
}

const linkClassName =
	"flex items-center gap-1.5 rounded px-2 py-1 font-[Rajdhani,sans-serif]! text-[14px]! text-white! transition-colors hover:bg-[rgb(34,38,43)] [&_svg]:text-[rgb(44,220,188)]";

const fontFaces = `@font-face {
	font-family: "Rajdhani";
	src: url("/fonts/rajdhani-v16-latin-regular.woff2") format("woff2");
	font-weight: 400;
	font-display: swap;
}
@font-face {
	font-family: "Rajdhani";
	src: url("/fonts/rajdhani-v16-latin-500.woff2") format("woff2");
	font-weight: 500;
	font-display: swap;
}`;

export const AdminBar = ({ cmsURL, collectionSlug, id, blogId, draft }: AdminBarProps) => {
	const pathname = usePathname();
	const [user, setUser] = useState<{ id: string; email: string } | null>(null);
	const [inIframe, setInIframe] = useState(false);
	const devEnv = process.env.NODE_ENV === "development";

	useEffect(() => {
		setInIframe(window.self !== window.top);
	}, []);

	useEffect(() => {
		fetch(`${cmsURL}/api/users/me`, { credentials: "include" })
			.then((res) => res.json())
			.then((data) => setUser(data.user ?? null))
			.catch(() => setUser(null));
	}, [cmsURL]);

	useEffect(() => {
		document.body.classList.toggle("admin-bar-visible", !inIframe && (user !== null || draft === true));
	}, [user, draft, inIframe]);

	if (inIframe || (!user && !draft)) return null;

	return (
		<div className="h-(--admin-bar-height) bg-[rgb(8,10,15)] font-[Rajdhani,sans-serif] text-white flex items-center">
			<style>{fontFaces}</style>

			<div className="flex mx-auto max-w-7xl items-center justify-between px-2 w-full">
				<nav className="flex items-center gap-1">
					<a href={`${cmsURL}/admin`} className={linkClassName}>
						<Image src={logo} alt="Marketsuite" width={20} height={20} />
					</a>

					<span className={cn("px-2 py-0.5 rounded text-xs font-medium", draft ? "bg-amber-500" : "text-black bg-[rgb(44,220,188)]")}>
						{draft ? "PREVIEW" : "LIVE"}
					</span>

					{devEnv && <span className="px-2 py-0.5 border text-amber-500  border-amber-500 rounded text-xs font-medium">DEVELOPMENT</span>}

					{user && (
						<a href={`${cmsURL}/admin/collections/users/${user.id}`} target="_blank" rel="noopener noreferrer" className={linkClassName}>
							<User size={14} />
							{user.email}
						</a>
					)}
				</nav>

				<nav className="flex items-center gap-1">
					{draft && (
						<a href={`/next/exit-preview?redirect=${encodeURIComponent(pathname)}`} className={linkClassName}>
							<EyeOff size={14} />
							Exit preview
						</a>
					)}
					{collectionSlug && id && (
						<a href={`${cmsURL}/admin/collections/${collectionSlug}/${id}`} target="_blank" rel="noopener noreferrer" className={linkClassName}>
							<Pencil size={14} />
							{collectionSlug === "page-templates" ? "Edit page template" : "Edit page"}
						</a>
					)}
					{blogId && (
						<a href={`${cmsURL}/admin/collections/blogs/${blogId}`} target="_blank" rel="noopener noreferrer" className={linkClassName}>
							<Pencil size={14} />
							Edit blog
						</a>
					)}
					{collectionSlug === "pages" && (
						<a href={`${cmsURL}/admin/collections/pages/create`} target="_blank" rel="noopener noreferrer" className={linkClassName}>
							<Plus size={14} />
							New page
						</a>
					)}
					{user && (
						<a href={`${cmsURL}/admin/logout`} target="_blank" rel="noopener noreferrer" aria-label="Logout" className={linkClassName}>
							<LogOut size={14} />
						</a>
					)}
				</nav>
			</div>
		</div>
	);
};
